import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messages", "input", "submitBtn", "credits", "modeRadio", "contactPicker", "anonFields", "resetBtn", "emptyState", "chatArea", "mdContent", "pendingNotice"]
  static values = { url: String, csrf: String, conversationId: { type: Number, default: 0 }, pendingBatch: { type: String, default: "" } }

  connect() {
    this.conversation = []
    this.sending = false
    this.pendingMorph = false
    this.rehydrateMarkdown()
    // Scroll to bottom if loading existing conversation
    if (this.hasMessagesTarget) {
      setTimeout(() => this.scrollToBottom(), 50)
    }
    // Server may have rendered the input already disabled. Sync the lock state
    // so subsequent unlock calls work correctly.
    if (this.pendingBatchValue) this.lockChat(this.pendingBatchValue)

    // If the server rendered an inline processing indicator (page was loaded
    // mid-batch), start the safety-net poll so completion is caught.
    const existingPoll = this.element.querySelector("[data-batch-poll]")
    if (existingPoll) this.startBatchStatusPoll(existingPoll.dataset.batchPoll)

    // Guard against Turbo morphs firing in the middle of a send. While sending,
    // the DOM holds optimistically-appended user/assistant messages and a URL
    // that hasn't yet been updated with the new conversation_id. A morph at
    // that moment fetches a stale URL, gets rendered as the empty-state page,
    // and wipes everything. Defer any morph attempt until the send completes.
    this.beforeRenderHandler = (event) => {
      if (!this.sending) return
      event.preventDefault()
      this.pendingMorph = true
    }
    document.addEventListener("turbo:before-render", this.beforeRenderHandler)

    // After any Turbo render/morph, re-hydrate markdown. When data-turbo-permanent
    // fails to preserve a JS-rendered message (e.g. because the element wasn't in
    // Turbo's prior "known good" DOM), the server's empty version wins and we need
    // to re-render from data-raw to get the content back.
    this.renderHandler = () => this.rehydrateMarkdown()
    document.addEventListener("turbo:render", this.renderHandler)
    document.addEventListener("turbo:morph", this.renderHandler)
  }

  disconnect() {
    if (this.beforeRenderHandler) {
      document.removeEventListener("turbo:before-render", this.beforeRenderHandler)
    }
    if (this.renderHandler) {
      document.removeEventListener("turbo:render", this.renderHandler)
      document.removeEventListener("turbo:morph", this.renderHandler)
    }
    this.stopBatchStatusPoll()
  }

  // Renders markdown for any oscar-md div whose data-raw is populated but
  // whose innerHTML is empty — covers initial load AND post-morph recovery.
  rehydrateMarkdown() {
    this.mdContentTargets.forEach(el => {
      const raw = el.dataset.raw
      if (!raw) return
      if (el.innerHTML.trim() === "") el.innerHTML = this.renderMarkdown(raw)
    })
  }

  // Stimulus calls this whenever data-oscar-console-pending-batch-value changes.
  // After a Turbo morph caused by the batch queue completing, the server re-renders
  // with no pending lock — this callback unlocks the chat without requiring a refresh.
  pendingBatchValueChanged(newValue, oldValue) {
    if (oldValue && !newValue) this.unlockChat()
  }

  lockChat(batchQueueId) {
    this.pendingBatchValue = batchQueueId || ""
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
      this.inputTarget.placeholder = "Resolve pending batch above to continue…"
    }
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true
    if (this.hasPendingNoticeTarget) this.pendingNoticeTarget.classList.remove("hidden")
  }

  unlockChat() {
    this.pendingBatchValue = ""
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
      this.inputTarget.placeholder = "Ask Oscar anything..."
    }
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
    if (this.hasPendingNoticeTarget) this.pendingNoticeTarget.classList.add("hidden")
  }

  sendSuggestion(event) {
    const suggestion = event.currentTarget.dataset.suggestion
    if (!suggestion) return
    this.inputTarget.value = suggestion
    this.inputTarget.form.requestSubmit()
  }

  async send(event) {
    event.preventDefault()

    const content = this.inputTarget.value.trim()
    if (!content) return

    this.sending = true
    this.pendingMorph = false

    this.inputTarget.value = ""
    this.conversation.push({ role: "user", content })

    // Switch from the empty-state layout to the chat layout only if we're
    // actually starting a brand-new conversation. Check the DOM (not the JS
    // array, which is always empty on page load) so we don't wipe existing
    // messages when sending into an already-loaded conversation.
    const isFirstMessageInSession = this.messagesTarget.children.length === 0
    if (isFirstMessageInSession) {
      if (this.hasResetBtnTarget) this.resetBtnTarget.classList.remove("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
      if (this.hasChatAreaTarget) this.chatAreaTarget.classList.remove("hidden")
    }

    this.appendMessage("user", content)
    this.showTyping()
    this.setLoading(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue
        },
        body: JSON.stringify({
          messages: this.conversation,
          contact_type: this.getMode() === "prospect" ? "prospect" : null,
          contact_id: this.getContactId(),
          test_phone: this.getTestPhone(),
          conversation_id: this.conversationIdValue || null
        })
      })

      this.removeTyping()

      if (response.ok) {
        const data = await response.json()
        if (!data.content) {
          this.appendError("Oscar returned an empty response. Please try again.")
          this.conversation.pop()
          return
        }
        this.conversation.push({ role: "assistant", content: data.content })
        // Re-tag the optimistically appended user message with its real ID so
        // morph from the broadcast can match it instead of wiping it.
        this.tagLastUserMessage(data.user_message_id)
        this.appendMessage("assistant", data.content, data.pending_action, data.assistant_message_id)
        if (data.pending_action?.type === "batch_queue" && data.pending_action.batch_queue_id) {
          this.lockChat(data.pending_action.batch_queue_id)
          // Flag so we request a fresh page render after send completes. The
          // server-rendered `turbo_stream_from` for the new queue only appears
          // after a render — without this, broadcasts for this batch never
          // reach the page and the user sees a loader forever.
          this.needsSubscribeRefresh = true
        }
        // Persist conversation ID for subsequent messages
        if (data.conversation_id && !this.conversationIdValue) {
          this.conversationIdValue = data.conversation_id
          const url = new URL(window.location)
          url.searchParams.set("conversation_id", data.conversation_id)
          history.replaceState(null, "", url)
          // Add to sidebar if present
          this.addToSidebar(data.conversation_id, data.conversation_title)
        }
        if (data.debug) this.appendDebug(data.debug)
        if (Array.isArray(data.back_stops) && data.back_stops.length > 0) this.appendBackStops(data.back_stops)
        this.updateCredits(data.credits_consumed)
      } else {
        const data = await response.json().catch(() => ({}))
        this.appendError(data.error || "Something went wrong. Please try again.")
        this.conversation.pop()
      }
    } catch (err) {
      this.removeTyping()
      console.error("[Oscar]", err)
      this.appendError(err.message || "Network error. Please try again.")
      this.conversation.pop()
    } finally {
      this.setLoading(false)
      if (!this.inputTarget.disabled) this.inputTarget.focus()
      this.sending = false
      // Post-send refreshes: either a suppressed morph or a new batch queue
      // that needs a server-side `turbo_stream_from` element to subscribe.
      // Both are resolved the same way — one Turbo.visit morph that re-renders
      // with current DB state. data-turbo-permanent keeps the markdown intact.
      if ((this.pendingMorph || this.needsSubscribeRefresh) && typeof window.Turbo?.visit === "function") {
        this.pendingMorph = false
        this.needsSubscribeRefresh = false
        window.Turbo.visit(window.location.href, { action: "replace" })
      }
    }
  }

  // Called by SelectableCardGroupComponent via data-action="change->oscar-console#toggle"
  toggle() {
    const mode = this.getMode()
    if (this.hasContactPickerTarget) {
      this.contactPickerTarget.classList.toggle("hidden", mode !== "contact")
    }
    if (this.hasAnonFieldsTarget) {
      this.anonFieldsTarget.classList.toggle("hidden", mode !== "prospect")
    }
    this.resetConversation()
  }

  getMode() {
    const checked = this.modeRadioTargets.find(r => r.checked)
    return checked ? checked.value : "prospect"
  }

  getContactId() {
    if (this.getMode() !== "contact") return null
    const input = this.element.querySelector('input[name="console[contact_id]"]')
    return input ? input.value : null
  }

  getTestPhone() {
    if (this.getMode() !== "prospect") return null
    const input = this.element.querySelector('input[name="console[test_phone]"]')
    return input ? input.value : null
  }

  resetConversation() {
    this.conversation = []
    this.conversationIdValue = 0
    this.messagesTarget.innerHTML = ""
    if (this.hasResetBtnTarget) this.resetBtnTarget.classList.add("hidden")
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    if (this.hasChatAreaTarget) this.chatAreaTarget.classList.add("hidden")
    // Clear URL param
    const url = new URL(window.location)
    url.searchParams.delete("conversation_id")
    history.replaceState(null, "", url)
  }

  addToSidebar(id, title) {
    const sidebar = document.getElementById("oscar-chat-sidebar-list")
    if (!sidebar) return
    const existing = sidebar.querySelector(`[data-conversation-id="${id}"]`)
    if (existing) return
    const link = document.createElement("a")
    link.href = `${window.location.pathname}?conversation_id=${id}`
    link.dataset.conversationId = id
    link.className = "block px-3 py-2 text-sm text-white bg-gray-700/50 rounded-lg truncate"
    link.textContent = title || "New conversation"
    sidebar.prepend(link)
  }

  appendMessage(role, content, pendingAction = null, messageId = null) {
    const isUser = role === "user"
    const div = document.createElement("div")
    // Match the server-rendered structure exactly so Turbo morph can reconcile
    // by ID instead of position. Without IDs, morph wipes JS-appended messages
    // when a broadcast (e.g. batch_queue refresh) fires before the next page load.
    div.className = `flex ${isUser ? "justify-end" : "justify-start"}`
    if (messageId) div.id = `msg-${messageId}`

    const rendered = isUser ? this.escapeHtml(content) : this.renderMarkdown(content)
    const actionsHtml = !isUser && pendingAction ? this.renderPendingActionButtons(pendingAction) : ""

    // Include data-raw on the JS-inserted markdown div. If Turbo morph ever
    // replaces this element with an empty server-rendered version, the
    // turbo:render handler re-renders the markdown from data-raw. We only
    // need to escape attribute delimiters — the renderMarkdown function
    // handles markdown syntax itself.
    const rawForAttr = !isUser ? content.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;") : ""
    const contentDiv = isUser
      ? `<div class="whitespace-pre-wrap">${rendered}</div>`
      : `<div${messageId ? ` id="msg-${messageId}-md" data-turbo-permanent` : ""} class="oscar-md" data-oscar-console-target="mdContent" data-raw="${rawForAttr}">${rendered}</div>`

    div.innerHTML = `
      <div class="max-w-[80%] rounded-lg px-4 py-2 text-sm ${
        isUser
          ? "bg-primary-600 text-white"
          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
      }">
        ${contentDiv}
        ${actionsHtml}
      </div>
    `
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  // The user message was appended optimistically (before the API call returned)
  // so it has no ID. Once the response gives us the real ID, tag it so morph
  // can match it. We assume it's the last user-aligned message in the container.
  tagLastUserMessage(id) {
    if (!id || !this.hasMessagesTarget) return
    const userMessages = this.messagesTarget.querySelectorAll(".flex.justify-end")
    const last = userMessages[userMessages.length - 1]
    if (last && !last.id) last.id = `msg-${id}`
  }

  renderPendingActionButtons(action) {
    if (action.type !== "batch_queue") return ""
    const id = this.escapeHtml(action.batch_queue_id)
    const total = parseInt(action.total_items || 0, 10)
    const isSingle = total === 1
    const reviewUrl = `/admin/batch_queues/${id}`

    // Single-item proposals: the chat message IS the review. Just commit or cancel.
    // Bulk proposals: warn (Dangerous) + offer item-by-item Manual Review page.
    const commitButton = isSingle
      ? `<button type="button"
           data-action="click->oscar-console#commitBatch"
           data-batch-queue-id="${id}"
           class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-700 transition-colors">
           Commit
         </button>`
      : `<button type="button"
           data-action="click->oscar-console#commitBatch"
           data-batch-queue-id="${id}"
           class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 transition-colors">
           <svg class="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/></svg>
           Commit (Dangerous)
         </button>`

    const reviewButton = isSingle ? "" : `
      <a href="${reviewUrl}"
        class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 transition-colors">
        <svg class="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
        Manual Review
      </a>`

    const headline = isSingle
      ? ""
      : `<p class="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">${total} changes pending — choose how to proceed:</p>`

    return `
      <div class="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600 space-y-2" data-batch-actions="${id}" data-single="${isSingle}">
        ${headline}
        <div class="flex flex-col sm:flex-row gap-2">
          ${commitButton}
          ${reviewButton}
          <button type="button"
            data-action="click->oscar-console#cancelBatch"
            data-batch-queue-id="${id}"
            class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    `
  }

  async commitBatch(event) {
    const btn = event.currentTarget
    const id = btn.dataset.batchQueueId
    const container = this.messagesTarget.querySelector(`[data-batch-actions="${id}"]`)
    const isSingle = container?.dataset?.single === "true"
    const confirmMsg = isSingle
      ? "Confirm this change? It will be applied immediately."
      : "Commit this batch immediately? All changes will be applied. This cannot be undone."
    if (!confirm(confirmMsg)) return
    await this.batchAction(`/admin/batch_queues/${id}/commit`, id, { mode: "thinking" })
  }

  async cancelBatch(event) {
    const btn = event.currentTarget
    const id = btn.dataset.batchQueueId
    const container = this.messagesTarget.querySelector(`[data-batch-actions="${id}"]`)
    const isSingle = container?.dataset?.single === "true"
    const confirmMsg = isSingle ? "Cancel this change?" : "Discard this batch? The proposed changes will be deleted."
    if (!confirm(confirmMsg)) return
    await this.batchAction(`/admin/batch_queues/${id}/discard`, id, { mode: "text", text: "Canceled." })
  }

  // mode: "thinking" → remove buttons, show standalone thinking dots below
  //   the bubble (the committing job will post a follow-up message).
  // mode: "text"     → replace buttons with a small label (terminal action —
  //   discard — no follow-up coming).
  async batchAction(url, id, feedback) {
    const container = this.messagesTarget.querySelector(`[data-batch-actions="${id}"]`)
    if (container) {
      container.querySelectorAll("button, a").forEach(b => b.setAttribute("disabled", "true"))
    }
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfValue,
          "Accept": "application/json"
        }
      })
      if (response.ok || response.status === 302 || response.redirected) {
        if (container) {
          if (feedback.mode === "thinking") {
            // Remove the action footer and insert an inline "Processing…"
            // indicator after the message. The Turbo morph (triggered when
            // the batch completes) will remove this element and render the
            // follow-up message in its place. Polling is a safety net in
            // case cable broadcasts race or drop.
            this.insertThinkingBubble(container, id)
            container.remove()
          } else {
            container.innerHTML = `<p class="text-xs font-medium text-gray-500 dark:text-gray-400">${feedback.text}</p>`
          }
        }
        if (this.pendingBatchValue === id) this.unlockChat()
      } else {
        if (container) {
          container.querySelectorAll("button, a").forEach(b => b.removeAttribute("disabled"))
        }
        alert("Action failed. Please try again or use the Batch Queue page.")
      }
    } catch (err) {
      console.error("[Oscar] batch action error", err)
      if (container) {
        container.querySelectorAll("button, a").forEach(b => b.removeAttribute("disabled"))
      }
    }
  }

  // Inserts an inline "Processing…" indicator AFTER the message row — no
  // bubble background, no border. Visually distinct from chat messages so
  // it reads as "tool is working," not "Oscar is saying something."
  // Also kicks off a status-polling safety net.
  insertThinkingBubble(actionContainer, batchQueueId) {
    const messageRow = actionContainer.closest(".flex.justify-start, .flex.justify-end")
    if (!messageRow || !messageRow.parentNode) return
    const thinking = document.createElement("div")
    thinking.className = "flex justify-start pl-1"
    thinking.dataset.batchPoll = batchQueueId
    thinking.innerHTML = `
      <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <div class="flex items-center gap-0.5">
          <span class="oscar-thinking-dot"></span>
          <span class="oscar-thinking-dot" style="animation-delay:0.2s"></span>
          <span class="oscar-thinking-dot" style="animation-delay:0.4s"></span>
        </div>
        <span class="italic">Processing…</span>
      </div>
    `
    messageRow.insertAdjacentElement("afterend", thinking)
    this.scrollToBottom()
    this.startBatchStatusPoll(batchQueueId)
  }

  // Safety-net poll: if cable broadcasts don't arrive (subscription race,
  // dropped connection, etc.), this catches the completion and forces a
  // refresh. 2s interval, gives up after 2 minutes.
  startBatchStatusPoll(batchQueueId) {
    if (!batchQueueId) return
    this.stopBatchStatusPoll()
    const started = Date.now()
    this.batchPollInterval = setInterval(async () => {
      if (Date.now() - started > 120_000) {
        this.stopBatchStatusPoll()
        return
      }
      try {
        const res = await fetch(`/admin/batch_queues/${batchQueueId}/status`, {
          headers: { "Accept": "application/json" }
        })
        if (!res.ok) return
        const data = await res.json()
        if (data.terminal) {
          this.stopBatchStatusPoll()
          if (typeof window.Turbo?.visit === "function") {
            window.Turbo.visit(window.location.href, { action: "replace" })
          }
        }
      } catch (_) { /* network blip — keep polling */ }
    }, 2000)
  }

  stopBatchStatusPoll() {
    if (this.batchPollInterval) {
      clearInterval(this.batchPollInterval)
      this.batchPollInterval = null
    }
  }

  renderMarkdown(text) {
    // Code blocks (~~~...~~~ or ```...```)
    text = text.replace(/(?:~~~|```)([\s\S]*?)(?:~~~|```)/g, (_, code) => {
      return `<pre class="bg-gray-900 dark:bg-gray-950 text-gray-100 rounded-lg p-4 my-2 overflow-x-auto text-xs font-mono whitespace-pre-wrap">${this.escapeHtml(code.trim())}</pre>`
    })

    // Process line by line for everything else
    const lines = text.split("\n")
    let html = ""
    let inList = false

    for (let line of lines) {
      // Headers
      if (line.match(/^### /)) {
        if (inList) { html += "</ul>"; inList = false }
        html += `<h4 class="font-semibold mt-3 mb-1">${this.escapeHtml(line.slice(4))}</h4>`
        continue
      }
      if (line.match(/^## /)) {
        if (inList) { html += "</ul>"; inList = false }
        html += `<h3 class="font-bold text-base mt-3 mb-1">${this.escapeHtml(line.slice(3))}</h3>`
        continue
      }
      if (line.match(/^# /)) {
        if (inList) { html += "</ul>"; inList = false }
        html += `<h2 class="font-bold text-lg mt-3 mb-1">${this.escapeHtml(line.slice(2))}</h2>`
        continue
      }

      // List items
      if (line.match(/^\s*[-*]\s/)) {
        if (!inList) { html += '<ul class="list-disc pl-5 my-1 space-y-0.5">'; inList = true }
        const itemText = line.replace(/^\s*[-*]\s/, "")
        html += `<li>${this.inlineMarkdown(itemText)}</li>`
        continue
      }
      if (line.match(/^\s*\d+\.\s/)) {
        if (inList) { html += "</ul>"; inList = false }
        const itemText = line.replace(/^\s*\d+\.\s/, "")
        html += `<div class="ml-4">${this.inlineMarkdown(line)}</div>`
        continue
      }

      // Regular line
      if (inList) { html += "</ul>"; inList = false }
      if (line.trim() === "") {
        html += "<br>"
      } else {
        html += `<p class="my-1">${this.inlineMarkdown(line)}</p>`
      }
    }
    if (inList) html += "</ul>"
    return html
  }

  inlineMarkdown(text) {
    let s = this.escapeHtml(text)
    // Bold
    s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic
    s = s.replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Inline code
    s = s.replace(/`(.+?)`/g, '<code class="bg-gray-200 dark:bg-gray-600 px-1 rounded text-xs">$1</code>')
    return s
  }

  appendError(message) {
    const div = document.createElement("div")
    div.className = "flex justify-center"
    div.innerHTML = `
      <div class="rounded-lg px-4 py-2 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
        ${this.escapeHtml(message)}
      </div>
    `
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  showTyping() {
    const div = document.createElement("div")
    div.className = "flex justify-start"
    div.id = "oscar-typing"
    div.innerHTML = `
      <div class="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
        <span class="inline-flex gap-1">
          <span class="animate-bounce" style="animation-delay: 0ms">.</span>
          <span class="animate-bounce" style="animation-delay: 150ms">.</span>
          <span class="animate-bounce" style="animation-delay: 300ms">.</span>
        </span>
      </div>
    `
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  removeTyping() {
    const el = document.getElementById("oscar-typing")
    if (el) el.remove()
  }

  setLoading(loading) {
    // Respect the pending-batch lock — never re-enable the input if the chat
    // is paused waiting for a Commit / Cancel decision on a draft queue.
    const locked = this.pendingBatchValue && this.pendingBatchValue.length > 0
    this.submitBtnTarget.disabled = loading || locked
    this.inputTarget.disabled = loading || locked
  }

  updateCredits(consumed) {
    if (!this.hasCreditsTarget) return
    const current = this.creditsTarget.textContent.trim()
    if (current === "∞") return
    const remaining = Math.max(0, parseInt(current) - consumed)
    this.creditsTarget.textContent = remaining
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  appendBackStops(backStops) {
    // Surfaces hallucination back-stops in the test console so staff can
    // see what would happen in production. In production these proposals
    // get persisted as Tasks; here we just display them.
    const div = document.createElement("div")
    div.className = "mx-2 mb-2"
    const items = backStops.map(b => {
      const label = b.display_label || b.kind
      return `<li class="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300">
        <span class="mt-0.5">⚠️</span>
        <span><span class="font-medium uppercase tracking-wide">${this.escapeHtml(b.kind)}</span> — ${this.escapeHtml(label)}. In production this would auto-create a follow-up task.</span>
      </li>`
    }).join("")
    div.innerHTML = `
      <div class="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2">
        <ul class="space-y-1">${items}</ul>
      </div>
    `
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  appendDebug(debug) {
    const div = document.createElement("div")
    div.className = "mx-2 mb-2"

    let toolSection = ""
    if (debug.tool_calls && debug.tool_calls.length > 0) {
      const toolLines = debug.tool_calls.map(tc => {
        const args = JSON.stringify(tc.arguments, null, 2)
        let result = ""
        try { result = JSON.stringify(JSON.parse(tc.result), null, 2) } catch { result = tc.result }
        return `<div class="mb-2"><span class="font-medium text-amber-400">${this.escapeHtml(tc.tool)}</span>(${this.escapeHtml(args)})\n<span class="text-gray-500">→</span> ${this.escapeHtml(result)}</div>`
      }).join("")
      toolSection = `<div class="mt-2 border-t border-gray-700 pt-2"><div class="text-[10px] font-medium text-gray-400 mb-1">Tool Calls</div>${toolLines}</div>`
    }

    div.innerHTML = `
      <details class="group">
        <summary class="text-[10px] text-gray-500 cursor-pointer hover:text-gray-400 select-none">
          Debug · ${debug.model || "unknown"} · ${debug.input_tokens || 0} in / ${debug.output_tokens || 0} out
        </summary>
        <div class="mt-1 rounded bg-gray-900 border border-gray-700 p-3 text-[11px] font-mono text-gray-300 max-h-64 overflow-auto whitespace-pre-wrap">
          <div class="text-[10px] font-medium text-gray-400 mb-1">System Prompt</div>
          <div class="text-gray-400">${this.escapeHtml(debug.system_prompt || "")}</div>
          ${toolSection}
        </div>
      </details>
    `
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
